import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDashboardUrlState, type UseDashboardUrlStateResult } from '../../filter/useDashboardUrlState'
import { toDashboardFilter } from '../../filter/dashboardFilterReducer'
import type { WidgetProps } from '../../widgets/types'
import type { DashboardFilter } from '../../../../types/insights'

export interface UseJurisdictionAnalyticsResult extends UseDashboardUrlStateResult {
  filter: DashboardFilter
  widgetProps: (widgetKey: string) => WidgetProps
  filterFieldsOpen: boolean
  setFilterFieldsOpen: (open: boolean) => void
}

/**
 * IB-282 View 3 — thin composition hook, mirrors `DashboardScreen`'s own top-of-component logic
 * (URL-addressable filter state, per-widget prop shape) extracted into its own file so
 * `JurisdictionAnalyticsContainer` stays a one-line wire-up, same split View-2's `useCaseAnalytics`
 * / `CaseAnalyticsPanel` already established for this suite. Reuses `useDashboardUrlState` /
 * `toDashboardFilter` VERBATIM — they are already route-agnostic (keyed only to this route's own
 * `useSearchParams`), not View-1-specific, so no fork was needed.
 */
export function useJurisdictionAnalytics(): UseJurisdictionAnalyticsResult {
  const navigate = useNavigate()
  const urlState = useDashboardUrlState()
  const [filterFieldsOpen, setFilterFieldsOpen] = useState(false)

  const filter: DashboardFilter = toDashboardFilter(urlState.state)

  function onDrill(rsql: string): void {
    navigate(`/cases/filings${rsql ? `?filter=${encodeURIComponent(rsql)}` : ''}`)
  }

  function widgetProps(widgetKey: string): WidgetProps {
    return {
      filter,
      skipRefetch: urlState.state.crossFilterOrigin === widgetKey,
      onCrossFilter: (axis, value) =>
        urlState.dispatch({ type: 'ADD_CROSSFILTER', axis, value, sourceWidget: widgetKey }),
      onDrill,
    }
  }

  return { ...urlState, filter, widgetProps, filterFieldsOpen, setFilterFieldsOpen }
}
